import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { NavigationService } from '@treasury/core/navigation';
import { AccountService } from '@treasury/domain/account';
import { type ApprovalCounts } from '@treasury/domain/approvals/types';
import { DashboardService as dashboardService } from '@treasury/domain/dashboard/dashboard.service';
import {
    FeatureVisibilityService,
    PwaVisibilityFeature,
} from '@treasury/domain/services/feature-visibility';
import { CurrentUser } from '@treasury/domain/user/current-user.entity';
import { TmContainer } from '@treasury/presentation';
import {
    achIcon,
    billIcon,
    checkRangeIcon,
    transferIcon,
    userIcon,
    wiresIcon,
} from '@treasury/presentation/assets/icons';
import '@treasury/presentation/components/tm-card';
import '@treasury/presentation/components/tm-widget-button';
import { css, html, nothing } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';

export const tagName = 'approval-widget';
@customElement(tagName)
export class ApprovalWidget extends TmContainer {
    @property({ type: Boolean })
    public disabled = false;

    @InjectProperty({
        async: true,
        token: FeatureVisibilityService,
    })
    declare private featureVisibilityServicePromise: Promise<FeatureVisibilityService>;

    @state()
    private featureVisibilityService?: FeatureVisibilityService;

    @InjectProperty()
    declare private dashService: dashboardService;

    @InjectProperty()
    declare private navService: NavigationService;

    @InjectProperty()
    declare private userClient: AccountService;

    @state()
    private user?: CurrentUser;

    @state()
    private approvalCounts: ApprovalCounts = {
        ach: 0,
        wire: 0,
        transfer: 0,
        loanpayment: 0,
        stoppayment: 0,
        user: 0,
    };

    protected render() {
        return html`<tm-card .header=${'Approvals'}> ${this.renderApprovalIcons()} </tm-card> `;
    }

    protected async firstUpdated() {
        await this.tryFetch(
            () =>
                Promise.all([
                    this.featureVisibilityServicePromise,
                    this.dashService.getPendingApprovalCounts(),
                    this.userClient.getCurrentUser(),
                ]),
            resp => {
                [this.featureVisibilityService, this.approvalCounts, this.user] = resp;
            },
            err => {
                this.notificationService.renderError(err as Error);
                this.loading = false;
            }
        );
    }

    private isFeatureVisible(feature: PwaVisibilityFeature): boolean {
        return this.featureVisibilityService
            ? this.featureVisibilityService.isFeatureVisible(feature)
            : false;
    }

    private routeToApprovalType(type: string) {
        this.navService.navigate(`/pending-approvals/${type}`);
    }

    private handleKeyUp(event: KeyboardEvent, route: string) {
        const { key } = event;
        if (key === 'Return') {
            this.routeToApprovalType(route);
        }
    }

    private renderAchActionButton() {
        if (this.isFeatureVisible(PwaVisibilityFeature.ACH)) {
            return html`<tm-widget-button
                .label=${'ACH'}
                .icon=${achIcon}
                .action=${() => this.routeToApprovalType('ach')}
                .keyup=${(event: KeyboardEvent) => this.handleKeyUp(event, 'ach')}
                .approvals=${this.approvalCounts.ach}
                .disabled=${this.disabled}
            ></tm-widget-button>`;
        }
        return nothing;
    }

    private renderTransfersActionButton() {
        if (this.isFeatureVisible(PwaVisibilityFeature.Transfers)) {
            return html`<tm-widget-button
                .label=${'Transfers'}
                .icon=${transferIcon}
                .action=${() => this.routeToApprovalType('transfer')}
                .keyup=${(event: KeyboardEvent) => this.handleKeyUp(event, 'transfer')}
                .approvals=${this.approvalCounts.transfer}
                .disabled=${this.disabled}
            ></tm-widget-button>`;
        }
        return nothing;
    }

    private renderWiresActionButton() {
        if (this.isFeatureVisible(PwaVisibilityFeature.Wires)) {
            return html`<tm-widget-button
                .label=${'Wires'}
                .icon=${wiresIcon}
                .action=${() => this.routeToApprovalType('wire')}
                .keyup=${(event: KeyboardEvent) => this.handleKeyUp(event, 'wire')}
                .approvals=${this.approvalCounts.wire}
                .disabled=${this.disabled}
            ></tm-widget-button>`;
        }
        return nothing;
    }

    private renderLoansActionButton() {
        if (this.isFeatureVisible(PwaVisibilityFeature.Transfers)) {
            return html`<tm-widget-button
                .label=${'Loans'}
                .icon=${billIcon}
                .action=${() => this.routeToApprovalType('loanpayment')}
                .keyup=${(event: KeyboardEvent) => this.handleKeyUp(event, 'loanpayment')}
                .approvals=${this.approvalCounts.loanpayment}
                .disabled=${this.disabled}
            ></tm-widget-button>`;
        }
        return nothing;
    }

    private renderStopPaymentActionButton() {
        if (this.isFeatureVisible(PwaVisibilityFeature.StopPayments)) {
            return html`<tm-widget-button
                .label=${'Stop Payments'}
                .icon=${checkRangeIcon}
                .action=${() => this.routeToApprovalType('stoppayment')}
                .keyup=${(event: KeyboardEvent) => this.handleKeyUp(event, 'stoppayment')}
                .approvals=${this.approvalCounts.stoppayment}
                .disabled=${this.disabled}
            ></tm-widget-button>`;
        }
        return nothing;
    }

    private renderUsersActionButton() {
        if (!this.user) {
            return nothing;
        }
        if (!this.user.isAdmin) {
            return nothing;
        }
        return html`<tm-widget-button
            .label=${'Users'}
            .icon=${userIcon}
            .action=${() => this.routeToApprovalType('user')}
            .approvals=${this.approvalCounts.user}
            .disabled=${this.disabled}
        ></tm-widget-button>`;
    }

    private renderApprovalIcons() {
        if (this.loading) {
            return html`<tm-loader card></tm-loader>`;
        }
        return html`<div class="grid grid-auto-fill gap-4 justify-items-center px-4">
            ${this.renderAchActionButton()} ${this.renderTransfersActionButton()}
            ${this.renderWiresActionButton()}
            ${this.renderLoansActionButton()}${this.renderStopPaymentActionButton()}
            ${this.renderUsersActionButton()}
        </div>`;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --tm-card-min-width: auto;
                    --tm-card-min-height: 150px;
                }
                .grid-auto-fill {
                    grid-template-columns: repeat(auto-fill, minmax(80px, 1fr));
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: ApprovalWidget;
    }
}
